import _ from 'lodash'
import moment from 'moment'
import classnames from 'classnames'
import React, { Component } from 'react'
import apis from 'browser/app/models/apis'
import { EventSubscription } from 'fbemitter'
import { browserHistory } from 'browser/history'
import { Store } from 'shared-libs/models/store'
import { Entity } from 'shared-libs/models/entity'
import { CalendarEvent, BlackoutDate } from './types'
import { SchemaIds } from 'shared-libs/models/schema'
import { Settings } from 'browser/app/models/settings'
import { AppointmentToolbar } from './appointment-toolbar'
import { EntityFormPanel } from '../entity/entity-form-panel'
import { AppointmentEventCell } from './appointment-event-cell'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { SELECTED_CALENDARS_KEY } from './appointment-calendar-selector'
import { checkTimeSlotAvailability, TimeSlotAvailability } from './utils'
import { evaluateExpression, evaluateFilters } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { IntervalCollectionManager } from './interval-collection-manager'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { HoursOfOperation } from 'shared-libs/generated/server-types/entity/locationEntity'
import { getLocalStorageItems, putLocalStorageItem } from 'browser/contexts/local-storage/utils'
import { AppointmentEntity } from 'shared-libs/generated/server-types/entity/yms/carrierAppointmentEntity'
import {
  Calendar,
  momentLocalizer,
  View,
  SlotInfo,
  ToolbarProps,
  CalendarProps,
} from 'react-big-calendar'

import './_appointment-calendar.scss'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss'

type Appointment = Entity & AppointmentEntity

type ScheduleByResourceId = {
  [resourceId: string]: HoursOfOperation
}

// TODO  - This function can be used to shift the calendar to be relative to other timezones (without changing the global moment TZ).
//         Eventually we'll probably want a selector.

// Usage - const localizer = momentLocalizer(momentWrappedTimezone(timezoneId))

// function momentWrappedTimezone(timezone: string): typeof moment {
//   return new Proxy(moment, {
//     apply(target, thisArg, args) {
//       if (args.length === 0) {
//         return target().tz(timezone)
//       } else if (typeof args[0] === 'string') {
//         return (target.tz as any)(...args, timezone)
//       } else {
//         return target(args[0]).tz(timezone)
//       }
//     }
//   })
// }

const localizer = momentLocalizer(moment)

type MyCalendarProps = CalendarProps<CalendarEvent>
const TypedCalendar = Calendar as React.ComponentType<MyCalendarProps>
const DraggableCalendar = withDragAndDrop(TypedCalendar)

const CURRENT_VIEW_KEY = 'vector.appointment-calendar.currentView'
const LAST_USED_CALENDAR_ENTITY_ID_KEY = 'vector.appointment-calendar.lastCalendarEntityId'

const DEFAULT_ADD_ENTITY_ID = SchemaIds.YMS_CARRIER_APPOINTMENT

// module-level cache, not ideal but preserves some cache behavior for when the user navigates to
// and from the calendar and doesn't expect it to keep rubber-banding back to today's date. If we
// ever have multiple calendar instances or need to better support log out across multiple users,
// elevating this to a map may be useful.
let cachedCurrentDate: Date | null = null

interface RGB {
  r: number,
  g: number,
  b: number,
}

function rgbToHex(rgb: RGB) {
  return "#" + (1 << 24 | rgb.r << 16 | rgb.g << 8 | rgb.b).toString(16).slice(1);
}

function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return {
    r: result ? parseInt(result[1], 16) : 0,
    g: result ? parseInt(result[2], 16) : 0,
    b: result ? parseInt(result[3], 16) : 0,
  };
}

function darkenHex(hex: string, ratio=0.8) {
  const rgb = hexToRgb(hex)

  rgb.r *= ratio
  rgb.g *= ratio
  rgb.b *= ratio

  return rgbToHex(rgb)
}

export interface IAppointmentCalendarProps extends IBaseProps {
  defaultDate?: Date
  settings: Settings
  entitySchema: Entity
  frames: FramesManager
  filters?: any[]
  /**
   * Determines whether a given appointment can be edited by the current user. Defaults to a
   * firm ownership check. To override via UI JSON, use `mapActionsToProps` — the appointment
   * is passed as `props[0]`:
   *
   * @example
   * "mapActionsToProps": {
   *   "isAppointmentEditable": {
   *     "type": "expression",
   *     "value": "props[0]?.owner?.firm?.entityId == null || settings.getFirm()?.uniqueId === props[0]?.owner?.firm?.entityId"
   *   }
   * }
   */
  isAppointmentEditable?: (appointment: Appointment) => boolean
  addEntityId?: string
  addEntityDefaultValue?: any

  showCalendarFilter: boolean
  showStandingAppointmentCreation: boolean
}

interface IAppointmentCalendarState {
  loading: boolean
  error: string | null
  appointmentsByInterval: Record<string, Appointment[]>
  savedAppointments: Appointment[]
  hoursOfOperation: ScheduleByResourceId
  blackoutDates: BlackoutDate[]
  currentDate: Date
  currentView: View
  selectedCalendarIds: Record<string, boolean> | undefined
}

export class AppointmentCalendar extends Component<
  IAppointmentCalendarProps,
  IAppointmentCalendarState
> {
  public static defaultProps: Partial<IAppointmentCalendarProps> = {
    defaultDate: new Date(),
    addEntityId: DEFAULT_ADD_ENTITY_ID,
    showCalendarFilter: true,
    showStandingAppointmentCreation: true,
  }

  private calendarComponents = {
    toolbar: (props: ToolbarProps & IAppointmentCalendarProps) => (
      <AppointmentToolbar
        {...props}
        onCalendarsSelected={this.handleCalendarsSelected}
        onCreateStandingAppointment={this.handleCreateStandingAppointment}
      />
    ),
    event: AppointmentEventCell,
  }

  private collectionManager: IntervalCollectionManager
  private collectionSubscription: EventSubscription | null = null
  private provisionalEntity?: Appointment | null = null
  private entityCreatedSubscription: EventSubscription | null = null
  private entityChangeSubscription: EventSubscription | null = null
  private entityResetSubscription: EventSubscription | null = null

  constructor(props: IAppointmentCalendarProps) {
    super(props)

    const storedValues = getLocalStorageItems()
    const selectedCalendarIds = storedValues[SELECTED_CALENDARS_KEY]
    const savedView = storedValues[CURRENT_VIEW_KEY] as View | undefined

    this.state = {
      appointmentsByInterval: {},
      savedAppointments: [],
      loading: true,
      error: null,
      currentDate: new Date((cachedCurrentDate ?? props.defaultDate ?? new Date()).getTime()),
      currentView: savedView || 'week',
      // TODO: fetch from api
      hoursOfOperation: {},
      blackoutDates: [],
      selectedCalendarIds,
    }

    this.collectionManager = new IntervalCollectionManager(
      apis,
      '/1.0/entities/metadata/core_calendar_appointment.json',
      'core_calendar_appointment.timeWindow.start.dateTime',
      'core_calendar_appointment.timeWindow.end.dateTime',
      this.evaluateFilters(props.filters),
      [
        {
          path: 'core_calendar_appointment.timeWindow.start.dateTime',
          type: 'descending',
        },
        {
          path: 'creationDate',
          type: 'descending',
        },
      ]
    )
  }

  public componentDidMount() {
    this.collectionSubscription = this.collectionManager.addListener(
      IntervalCollectionManager.ON_LOAD_END,
      this.handleLoadEnd
    )
    this.entityCreatedSubscription = apis
      .getStore()
      .addListener(Store.RECORD_CREATED, (entity: Entity) => this.onExternalUpdate(Store.RECORD_CREATED, entity))
    this.entityChangeSubscription = apis
      .getStore()
      .addListener(Store.RECORD_CHANGED, (entity: Entity) => this.onExternalUpdate(Store.RECORD_CHANGED, entity))
    this.entityResetSubscription = apis
      .getStore()
      .addListener(Store.RECORD_RESET, (entity: Entity) => this.onExternalUpdate(Store.RECORD_RESET, entity))

    this.debouncedLoad()
  }

  public componentWillUnmount() {
    this.collectionSubscription?.remove()
    this.collectionManager.destroy()
    this.entityCreatedSubscription?.remove()
    this.entityChangeSubscription?.remove()
    this.entityResetSubscription?.remove()
  }

  public componentDidUpdate(prevProps: IAppointmentCalendarProps) {
    if (!_.isEqual(prevProps.filters, this.props.filters)) {
      this.collectionManager.setFilters(this.evaluateFilters(this.props.filters))
      this.setState({ appointmentsByInterval: {} }, () => {
        this.debouncedLoad()
      })
    }
  }

  public render() {
    const { className } = this.props
    const { loading, error, currentDate, currentView } = this.state

    if (error) {
      return (
        <div className={classnames('appointment-calendar-error', className)}>
          Error loading appointments: {error}
        </div>
      )
    }

    const events = this.expandEvents()

    return (
      <div className={classnames('appointment-calendar', className)}>
        {loading && (
          <div className="appointment-calendar__loading-overlay">
            <div className="appointment-calendar__loading-spinner">Loading...</div>
          </div>
        )}
        <DraggableCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          titleAccessor={(e) => e.title}
          date={currentDate}
          view={currentView as View}
          onNavigate={this.handleNavigate}
          onView={this.handleViewChange}
          onSelectEvent={this.handleSelectEvent}
          onSelectSlot={this.handleSelectSlot}
          onEventDrop={this.handleEventTimeChange}
          onEventResize={this.handleEventTimeChange}
          draggableAccessor={this.draggableAccessor}
          resizableAccessor={this.draggableAccessor}
          resizable={true}
          eventPropGetter={this.eventPropGetter}
          slotPropGetter={this.slotPropGetter}
          style={{ height: '100%' }}
          popup={true}
          selectable={!this.provisionalEntity}
          views={{
            month: true,
            week: true,
            day: true,
            agenda: true,
          }}
          components={this.calendarComponents}
          step={30}
          timeslots={2}
          formats={{
            timeGutterFormat: 'HH:mm',
            eventTimeRangeFormat: () => '',
            agendaTimeRangeFormat: ({ start, end }) =>
              `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
            dayHeaderFormat: (date: Date) => moment(date).format('ddd D'),
            dayFormat: (date: Date) => moment(date).format('ddd D'),
          }}
          messages={{
            allDay: 'All Day',
            previous: '‹ Previous',
            next: 'Next ›',
            today: 'Today',
            month: 'Month',
            week: 'Week',
            day: 'Day',
            agenda: 'Agenda',
            date: 'Date',
            time: 'Time',
            event: 'Appointment',
            noEventsInRange: 'No appointments in this range.',
            showMore: (total) => `+${total} more`,
          }}
        />
      </div>
    )
  }

  private draggableAccessor = (event: CalendarEvent): boolean => {
    if (event.isProvisional) {
      return true
    }
    if (!this.isAppointmentEditable(event.appointment)) {
      return false
    }
    // only allow drag/resize when no entity-detail-card is open, or the detail card corresponds to
    // the dragged appointment, to prevent weird conflicts with cancel/discard/save.
    const openEntityId = this.getOpenEntityId()
    return !openEntityId || openEntityId === event.appointment?.uniqueId
  }

  private evaluateEventMappings(event: CalendarEvent) {
    const facilityEdge = _.get(event, 'appointment.core_yms_carrierAppointment.facility')
      ?? _.get(event, 'appointment.core_yms_standingAppointmentInstance.facility')
    if (!facilityEdge) {
      return
    }

    const facility: any = apis.getStore().getRecord(facilityEdge.entityId)

    if (!facility) {
      return {}
    }

    // TODO -- only render if facility is in the same firm as the user, don't show this to carrier users
    const mappings = facility.core_yms_facility.appointmentSchedulingSettings?.calendarStatusMappings
    if (!mappings) {
      return {}
    }

    const matchedMapping = mappings.find((mapping: any) => {
      return evaluateExpression({ ...event.appointment }, mapping.condition.expression)
    })

    if (!matchedMapping) {
      return {}
    }

    return {
      style: {
        backgroundColor: matchedMapping.statusColor,
        color: darkenHex(matchedMapping.statusColor, 0.5),
        borderColor: darkenHex(matchedMapping.statusColor, 0.75),
      }
    }
  }

  private eventPropGetter = (event: CalendarEvent) => {
    if (event.isProvisional) {
      return {
        className: 'provisional',
      }
    }

    if (event.isStandingSlot) {
      return {
        className: 'standing-slot',
        title: `Standing: ${event.title}`,
      }
    }

    const appointment = event.appointment
    if (!appointment) {
      return {}
    }

    // TODO: make this more robust, this was just to quickly add support for the orange Draft color
    const status = appointment.core_calendar_appointment?.status
    const statusClass = status === 'Draft' ? 'status-draft' : undefined
    const evaluatedProps: any = this.evaluateEventMappings(event)

    return {
      className: statusClass,
      title: `${appointment.name}\nStatus: ${appointment.status}\nLocation: ${appointment.location?.displayName || 'N/A'}`,
      ...evaluatedProps
    }
  }

  private slotPropGetter = (date: Date, resourceId?: string) => {
    const { hoursOfOperation, blackoutDates } = this.state
    const availability = checkTimeSlotAvailability(
      resourceId,
      hoursOfOperation,
      blackoutDates,
      date
    )

    if (!availability.available && availability.reason === 'blackout') {
      return { className: 'time-slot-blackout' }
    }

    return {
      className: availability.available ? 'time-slot-available' : 'time-slot-unavailable',
    }
  }

  private onExternalUpdate = (event: string, entity: Entity) => {
    if (event === Store.RECORD_CREATED && entity?.hasMixin(SchemaIds.CALENDAR_APPOINTMENT)) {
      this.cacheNewAppointment(entity as Appointment)
      return
    }
    this.debouncedForceUpdate()
  }

  private debouncedForceUpdate = _.debounce(() => {
    this.forceUpdate()
  }, 100)

  private handleNavigate = (date: Date) => {
    cachedCurrentDate = new Date(date.getTime())
    this.setState({ currentDate: date }, () => this.debouncedLoad())
  }

  private handleViewChange = (view: View) => {
    this.setState({ currentView: view }, () => this.debouncedLoad())
    putLocalStorageItem(CURRENT_VIEW_KEY, view)
  }

  private debouncedLoad = _.debounce((force = false) => {
    const { currentDate, currentView } = this.state
    const { startDate, endDate } = this.getViewDateRange(currentDate, currentView)
    this.setState({ loading: true })
    this.collectionManager
      .load(startDate.toISOString(), endDate.toISOString(), force)
      .finally(() => {
        this.setState({ loading: false })
      })
  }, 100)

  private handleLoadEnd = async ({ key, entities }: { key: string; entities: Entity[] }) => {
    await this.loadMissingFacilities(entities)

    console.log(`handleLoadEnd: ${key}`)
    this.setState((prevState) => ({
      appointmentsByInterval: {
        ...prevState.appointmentsByInterval,
        [key]: entities as Appointment[],
      },
    }))
  }

  private getFacility(entity: any) {
    return _.get(entity, 'core_yms_carrierAppointment.facility')
      ?? _.get(entity, 'core_yms_standingAppointmentInstance.facility')
  }

  private async loadMissingFacilities(entities: Entity[]): Promise<void> {
    const store = apis.getStore()
    const facilityIds = _.uniq(entities.map(entity => this.getFacility(entity)?.entityId))
    const missingFacilityIds = facilityIds.filter(facilityId => _.isNil(store.getRecord(facilityId)))

    await store.findRecords(missingFacilityIds)
  }

  private handleCalendarsSelected = (calendarIds: Record<string, boolean>) => {
    this.setState({ selectedCalendarIds: calendarIds })
  }

  private handleSelectEvent = (event: CalendarEvent) => {
    const { frames } = this.props

    if (!event.appointment?.uniqueId) {
      return
    }

    const match = frames.getValue('match')

    const route = match.url
    const entityId = event.appointment.uniqueId
    const pathname = `${route}/entity/${entityId}`
    browserHistory.push(pathname)
  }

  private handleSelectSlot = async (slotInfo: SlotInfo) => {
    if (this.provisionalEntity) {
      return
    }

    const availability = this.computeSlotAvailability(slotInfo)
    if (!availability.available) {
      return
    }

    const storedValues = getLocalStorageItems()
    const lastSavedCalendarEntityId = storedValues[LAST_USED_CALENDAR_ENTITY_ID_KEY]

    const { addEntityDefaultValue } = this.props
    const defaultValue = {
      core_calendar_appointment: {
        timeWindow: {
          start: { dateTime: slotInfo.start.toISOString() },
          end: { dateTime: slotInfo.end.toISOString() },
        },
        ...(lastSavedCalendarEntityId && {
          calendar: {
            entityId: lastSavedCalendarEntityId,
          },
        }),
      },
      ...addEntityDefaultValue
    }

    // lazy load schema, if needed
    const { addEntityId } = this.props
    const isDefaultAddSchema = (addEntityId === DEFAULT_ADD_ENTITY_ID)
    await apis.getStore().getOrFetchRecord(addEntityId)

    const [entity] = (await EntityFormPanel.openAndResolveDependencies({
      defaultValue,
      entityId: addEntityId,
      settings: this.props.settings,
      onSave: isDefaultAddSchema ? this.cacheNewAppointment : _.noop,
      onClose: isDefaultAddSchema ? this.handleFormCancel : _.noop,
    })) as Appointment[]

    if (isDefaultAddSchema) {
      this.provisionalEntity = entity
    }
    this.forceUpdate()
  }

  private handleCreateStandingAppointment = async () => {
    const { settings } = this.props
    const entityId = SchemaIds.YMS_STANDING_APPOINTMENT_DEFINITION

    // lazy load just in case
    await apis.getStore().getOrFetchRecord(entityId)

    const userFacility = settings.getUser()?.user?.facility
    const defaultValue = userFacility
      ? { core_yms_standingAppointmentDefinition: { facility: userFacility } }
      : undefined

    EntityFormPanel.openAndResolveDependencies({
      entityId,
      settings,
      defaultValue,
      onSave: this.handleStandingAppointmentSave,
      onClose: this.handleFormCancel,
    })
  }

  private handleStandingAppointmentSave = async (_entity: Entity) => {
    // impose a fake delay to buy time for the instances to populate in ES in the happy path.
    await new Promise((resolve) => setTimeout(resolve, 4000))
    this.debouncedLoad(true)
  }

  /**
   * Track the saved appointment in local state so the calendar renders it immediately, without
   * waiting for ES to index it and return it via the collection manager.
   */
  private cacheNewAppointment = (entity: Appointment) => {
    this.setState((prevState) => ({
      savedAppointments: [...prevState.savedAppointments, entity],
    }))

    const savedCalendarEntityId = entity.get('core_calendar_appointment.calendar.entityId')
    if (savedCalendarEntityId) {
      putLocalStorageItem(LAST_USED_CALENDAR_ENTITY_ID_KEY, savedCalendarEntityId)
    }

    this.provisionalEntity = null
  }

  private handleFormCancel = () => {
    this.provisionalEntity = null

    this.forceUpdate()
  }

  private handleEventTimeChange = ({
    event,
    start,
    end,
  }: {
    event: CalendarEvent
    start: Date
    end: Date
    resourceId?: string
  }) => {
    if (event.isProvisional) {
      this.updateAppointmentTime(this.provisionalEntity, start, end)
      this.provisionalEntity && this.provisionalEntity.emit(Store.RECORD_CHANGED, this.provisionalEntity)
      this.forceUpdate()
      return
    }

    const appointment = event.appointment
    if (appointment?.uniqueId) {
      if (!this.isAppointmentEditable(appointment)) {
        return
      }
      this.updateAppointmentTime(appointment, start, end)

      if (this.getOpenEntityId()) {
        // ensure the calendar properly re-renders the event and doesn't snap the event back to its
        // previous time
        this.forceUpdate()
        // ensure any other UIs like the detail card see the update
        appointment.emit(Store.RECORD_CHANGED, appointment)
      } else {
        // open the details pane if not already open so the user can save the change
        const { frames } = this.props
        const match = frames.getValue('match')
        const route = match.url
        browserHistory.push(`${route}/entity/${appointment.uniqueId}`)
      }
    }
  }

  private updateAppointmentTime(entity: Entity | any, start: Date | string, end: Date | string) {
    entity.set('core_calendar_appointment.timeWindow.start.dateTime', new Date(start).toISOString())
    entity.set('core_calendar_appointment.timeWindow.end.dateTime', new Date(end).toISOString())
  }

  /**
   * Expands appointments into a list of calendar events according to recurrence rules, overrides,
   * and pending or provisional events.
   */
  private expandEvents(): CalendarEvent[] {
    const { appointmentsByInterval, savedAppointments: localData, selectedCalendarIds } = this.state

    const intervalData = _.flatMap(Object.values(appointmentsByInterval))
    if (_.isEmpty(intervalData) && _.isEmpty(localData)) {
      return []
    }

    const events: CalendarEvent[] = []

    const appointments = _.uniqBy([...intervalData, ...localData], 'uniqueId')

    for (const appointment of appointments) {
      const isStandingInstance = !!(appointment as any).core_yms_standingAppointmentInstance

      // skip covered standing instances (the covering carrier appointment will show instead)
      if (
        isStandingInstance &&
        (appointment as any).core_yms_standingAppointmentInstance?.isCovered
      ) {
        continue
      }

      const calendarId = appointment?.core_calendar_appointment?.calendar?.entityId

      // filter by selected calendars (only hide if explicitly set to false)
      if (selectedCalendarIds && calendarId && selectedCalendarIds[calendarId] === false) {
        continue
      }

      const name = appointment.core_calendar_appointment?.name
      const start = appointment.core_calendar_appointment?.timeWindow?.start?.dateTime
      const end = appointment.core_calendar_appointment?.timeWindow?.end?.dateTime

      if (!name || !start || !end) {
        // TODO - log problem if appropriate
        continue
      }

      events.push({
        id: appointment.uniqueId,
        title: name || (isStandingInstance ? 'Standing Slot' : 'Appointment'),
        start: new Date(start),
        end: new Date(end),
        appointment: appointment,
        isStandingSlot: isStandingInstance,
      })
    }


    let provisionalEvent: CalendarEvent | undefined
    const provisionalStart = (this.provisionalEntity as Appointment)?.core_calendar_appointment?.timeWindow?.start?.dateTime
    const provisionalEnd = (this.provisionalEntity as Appointment)?.core_calendar_appointment?.timeWindow?.end?.dateTime
    if (provisionalStart && provisionalEnd) {
      // add provisional event if it exists
      provisionalEvent = {
        id: 'provisional',
        title: 'New Event...',
        start: new Date(provisionalStart),
        end: new Date(provisionalEnd),
        isProvisional: true,
      }
    } else {
      // TODO - log problem if appropriate
    }

    return [...events, ...(provisionalEvent ? [provisionalEvent] : [])]
  }

  private getViewDateRange(
    currentDate: Date,
    currentView: string
  ): { startDate: Date; endDate: Date } {
    const momentDate = moment(currentDate)

    let startDate: Date
    let endDate: Date

    switch (currentView) {
      case 'month':
        startDate = momentDate.clone().startOf('month').startOf('week').toDate()
        endDate = momentDate.clone().endOf('month').endOf('week').toDate()
        break
      case 'week':
        startDate = momentDate.clone().startOf('week').toDate()
        endDate = momentDate.clone().endOf('week').toDate()
        break
      case 'day':
        startDate = momentDate.clone().startOf('day').toDate()
        endDate = momentDate.clone().endOf('day').toDate()
        break
      case 'agenda':
      default:
        startDate = momentDate.clone().startOf('week').toDate()
        endDate = momentDate.clone().startOf('week').add(30, 'days').toDate()
    }

    return { startDate, endDate }
  }

  private getOpenEntityId(): string | null {
    const match = browserHistory.location.pathname.match(/\/entity\/([^/]+)/)
    return match ? match[1] : null
  }

  private isAppointmentEditable(appointment: Appointment): boolean {
    if (this.props.isAppointmentEditable) {
      return this.props.isAppointmentEditable(appointment)
    }

    const firmId = this.props.settings.getFirm()?.uniqueId
    const ownerFirmId = (appointment as any).owner?.firm?.entityId
    if (!firmId || !ownerFirmId) {
      return true
    }
    return firmId === ownerFirmId
  }

  private evaluateFilters(filters?: any[]): any[] {
    return evaluateFilters(filters || [], { settings: this.props.settings, ...CustomFormulas })
  }

  private computeSlotAvailability(slotInfo: SlotInfo): TimeSlotAvailability {
    const { hoursOfOperation, blackoutDates } = this.state

    return checkTimeSlotAvailability(
      slotInfo.resourceId?.toString(),
      hoursOfOperation,
      blackoutDates,
      slotInfo.start,
      slotInfo.end
    )
  }
}
